import axios from "axios";
import { extendAlmaGlobal } from "../../utils";
import { deferredPromise } from "../../utils/deferred-promise";
import Service from "../service";
import { makeQuery } from "./query";
class MetaApiService extends Service {
    constructor(configuration, context) {
        super("Meta-API", context);
        if (!configuration) {
            throw new Error("Missing Meta-API configuration");
        }
        this.configuration = configuration;
        this.configuration.defaults.endpoint =
            configuration.defaults.endpoint ??
                "https://meta-api.almamedia.io/v2/graphql";
        this.configuration.defaults.timeout =
            configuration.defaults.timeout ?? 1200;
        this.apiResponses = new Map();
        extendAlmaGlobal({ getMetadata: this.getMetadata.bind(this) }, "metaApi");
        this.inflight = new Map();
        this.axiosInstance = axios.create();
    }
    static buildService(config) {
        return (context) => new Promise((resolve) => {
            const s = new MetaApiService(config, context);
            resolve(s);
        });
    }
    static makeCacheId(id, system, siteId) {
        return `${system}:${siteId}:${id}`;
    }
    static formatDateForMetaApi(date) {
        const yyyy = date.getFullYear().toString();
        const month = date.getMonth() + 1;
        let mm = month.toString();
        let dd = date.getDate().toString();
        if (mm.length === 1)
            mm = `0${mm}`;
        if (dd.length === 1)
            dd = `0${dd}`;
        return `${yyyy}-${mm}-${dd}`;
    }
    async getMetadata(id, url, issuedAt, system = this.configuration.defaults.contentSystem, siteId = this.configuration.defaults.siteId) {
        this.logger.logInfo(`Request for meta data with ${system}:${siteId}:${id}`);
        const recencyLimit = new Date("2018-01-01");
        const issueDate = new Date(issuedAt);
        if (issueDate.getTime() < recencyLimit.getTime()) {
            this.logger.logInfo(`Request for meta data is cancelled due the recency of article`);
            return undefined;
        }
        // Format date for meta-api
        const formattedIssueDate = MetaApiService.formatDateForMetaApi(issueDate);
        // Generate ID to match requests or cache
        const cacheId = MetaApiService.makeCacheId(id, system, siteId);
        // Check if we have cached this response
        const cachedResponse = this.getCachedResponse(cacheId);
        if (cachedResponse) {
            return cachedResponse.response;
        }
        // Check if the request is already in flight
        // Check in flight request array for ID
        this.logger.logInfo(`Checking if ${cacheId} is already requested`);
        const waitingRequests = this.inflight.get(cacheId);
        if (waitingRequests) {
            // If found, return promise that will be resolved later
            this.logger.logInfo(`${cacheId} is already requested, return deferred`);
            const dp = deferredPromise(() => this.getCachedResponse(cacheId)?.response);
            this.inflight.set(cacheId, [...waitingRequests, dp]);
            return dp;
        }
        this.logger.logInfo(`No waiting requests for ${cacheId}`);
        // Otherwise return request promise
        this.logger.logInfo(`Not requested already, make API call`);
        this.inflight.set(cacheId, []);
        const res = await this.fetchMetaData(id, system, siteId, url, formattedIssueDate);
        this.apiResponses.set(cacheId, {
            response: res,
            timestamp: Date.now(),
        });
        this.handlePendingRequests(cacheId);
        return res;
    }
    handlePendingRequests(cacheId) {
        const waiting = this.inflight.get(cacheId);
        if (waiting && waiting?.length > 0) {
            this.logger.logInfo(`Found ${waiting.length} waiting requests, resolving`);
            waiting.forEach((r) => r.resolve());
            this.logger.logInfo(`Clearing requests from inflight`);
            this.inflight.delete(cacheId);
        }
    }
    getCachedResponse(id) {
        // Look through the Map for now, later we can make this more granular
        this.logger.logInfo(`Checking runtime cache for ${id}`);
        const cachedResponse = this.apiResponses.get(id);
        if (cachedResponse && cachedResponse.timestamp - Date.now() < 60 * 10) {
            this.logger.logInfo(`Runtime cache hit for ${id}`);
            return cachedResponse;
        }
        this.logger.logInfo(`Could not find a cached response for ${id}`);
        return undefined;
    }
    async fetchMetaData(cid, system, siteId, url, issuedAt) {
        const cacheId = MetaApiService.makeCacheId(cid, system, siteId);
        this.logger.logInfo(`Fetching meta data for content ${cacheId}`);
        const analyticsPayload = { cid, system, siteId, url, issuedAt };
        let kws;
        const queryUrl = makeQuery(cid, system, siteId, url, issuedAt);
        try {
            this.context.core?.recordEvent("meta-api.data_requested.start", performance.now(), analyticsPayload);
            const res = await this.axiosInstance.get(`${this.configuration.defaults.endpoint}?query=${encodeURIComponent(queryUrl)}`, {
                timeout: this.configuration.defaults.timeout,
            });
            this.logger.logInfo(`Received meta api response for ${cacheId}`);
            if (res.data) {
                kws = res.data.data?.contentMeta.content.keywords;
                if (kws) {
                    this.context.core?.recordEvent("meta-api.data_requested.end", performance.now(), analyticsPayload);
                    return kws;
                }
            }
            if (res.data.errors) {
                const errors = res.data.errors
                    .map((e) => `${e.errorType}=${e.message}`)
                    .join(",");
                this.context.core?.recordEvent("meta-api.error", performance.now(), {
                    ...analyticsPayload,
                    errors,
                });
                throw new Error(errors);
            }
            this.context.core?.recordEvent("meta-api.data_requested.empty_response", performance.now(), analyticsPayload);
            return kws;
        }
        catch (e) {
            this.logger.logError(e);
            return kws;
        }
    }
}
export default MetaApiService;
//# sourceMappingURL=meta-api.js.map